import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import { useTheme } from '@material-ui/core/styles';
import Container from 'common/Container';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';
import handleOpenWhatsApp from 'services/whatsappService';
import { enProduccionConMemForegroundOnPage } from './enProduccionConMemReadableText';
import { EN_PRODUCCION_CON_MEM_LOGO_SRC, TOTAL_TOOLS_LOGO_SRC } from './brandAssets';
import { TOTAL_PROPOSAL } from './totalProposalContent';
import EnProduccionConMemProposalPlanCards from './components/EnProduccionConMemProposalPlanCards';

const LandingTotalEnProduccionConMem = ({ themeMode = 'light' }) => {
  const theme = useTheme();
  const headingColor = enProduccionConMemForegroundOnPage(theme);
  const accent = theme.palette.secondary.main;
  const p = TOTAL_PROPOSAL;

  return (
    <Box sx={{ pb: 8, pt: { xs: 2, md: 3 } }}>
      <Container>
        <Box sx={{ mb: 3 }}>
          <Link
            href="/enproduccionconmem"
            underline="hover"
            sx={{ color: headingColor, fontWeight: 600, '&:hover': { color: accent } }}
          >
            ← Media kit En Producción con MEM
          </Link>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
            gap: 2,
            mb: 4,
            maxWidth: 800,
          }}
        >
          <Box
            component="img"
            src={EN_PRODUCCION_CON_MEM_LOGO_SRC}
            alt="En Producción con MEM"
            sx={{
              maxWidth: 220,
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : '#fff',
              p: 1.5,
              borderRadius: 1,
              border: `2px solid ${accent}`,
            }}
          />
          <Typography
            variant="overline"
            sx={{ letterSpacing: 1.2, color: accent, fontWeight: 700 }}
          >
            {p.eyebrow}
          </Typography>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontWeight: 700,
              color: headingColor,
              lineHeight: 1.2,
            }}
          >
            {p.title}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
              color: headingColor,
              fontWeight: 600,
              opacity: 0.95,
              lineHeight: 1.35,
            }}
          >
            {p.concept}
          </Typography>
          <Box
            component="img"
            src={TOTAL_TOOLS_LOGO_SRC}
            alt="Total Tools"
            sx={{
              maxWidth: 220,
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              bgcolor: '#fff',
              p: 1.5,
              borderRadius: 1,
              border: `2px solid ${theme.palette.divider}`,
              mt: 0.5,
            }}
          />
        </Box>

        <Divider sx={{ mb: 5, borderColor: accent, opacity: 0.35 }} />

        <Box component="section" sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ fontFamily: 'Georgia, "Times New Roman", serif', color: headingColor, fontWeight: 700 }}
          >
            1. {p.sectionOneTitle}
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3, maxWidth: 800, lineHeight: 1.75 }}>
            {p.intro}{' '}
            <Link
              href={p.brandSiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ color: accent, fontWeight: 600 }}
            >
              {p.brandSiteLabel}
            </Link>
          </Typography>
          <Grid container spacing={3}>
            {p.editorialAxes.map((axis) => (
              <Grid item xs={12} md={4} key={axis.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    height: '100%',
                    borderLeft: `4px solid ${accent}`,
                    bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: headingColor, mb: 1 }}>
                    {axis.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.7 }}>
                    {axis.body}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <EnProduccionConMemProposalPlanCards
          themeMode={themeMode}
          overline={p.planExplainSection.overline}
          title={p.planExplainSection.title}
          subtitle={p.planExplainSection.subtitle}
          body={p.planExplainSection.body}
          cards={p.planExplainCards}
        />

        <Box component="section" sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ fontFamily: 'Georgia, "Times New Roman", serif', color: headingColor, fontWeight: 700 }}
          >
            2. {p.planTableHeading}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2, fontWeight: 600 }}>
            {p.planTitle}
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: 720 },
              mx: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              overflowX: 'auto',
            }}
          >
            <Table size="small" aria-label="Inversión mensual Plan TOTAL Liderazgo Operativo">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: headingColor }}>Servicio estratégico</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: headingColor }}>Detalle del alcance</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: headingColor, whiteSpace: 'nowrap' }}>
                    Inversión
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {p.planRows.map((row) => (
                  <TableRow key={row.service}>
                    <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>{row.service}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top', lineHeight: 1.65 }}>
                      {row.detail}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {row.amount}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2} sx={{ fontWeight: 800, color: headingColor }}>
                    Total mensual — {p.planTotalLabel}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: accent, fontSize: '1.1rem' }}>
                    {p.planTotalAmount}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
            Montos orientativos en USD al mes, según propuesta comercial.
          </Typography>
          <PriceVatDisclaimer />
        </Box>

        <Box component="section" sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ fontFamily: 'Georgia, "Times New Roman", serif', color: headingColor, fontWeight: 700 }}
          >
            3. {p.materialsTitle}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                  borderTop: `4px solid ${accent}`,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: headingColor, mb: 2 }}>
                  {p.radioScriptTitle}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontStyle: 'italic', lineHeight: 1.8 }}
                >
                  Voz de marca: «{p.radioScript}»
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                  borderTop: `4px solid ${accent}`,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: headingColor, mb: 2 }}>
                  {p.programTitle}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                  {p.programBullets.map((b, idx) => (
                    <Typography key={idx} component="li" variant="body2" sx={{ mb: 1.5, lineHeight: 1.75 }}>
                      {b}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Box component="section" sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ fontFamily: 'Georgia, "Times New Roman", serif', color: headingColor, fontWeight: 700 }}
          >
            4. {p.whyTitle}
          </Typography>
          <Grid container spacing={2}>
            {p.whyPoints.map((item, i) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: accent, mb: 1 }}>
                    {i + 1}. {item.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.75 }}>
                    {item.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            textAlign: 'center',
            bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: headingColor, mb: 2 }}>
            ¿Activamos el Plan de Liderazgo Operativo Total Tools con En Producción con MEM?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: 560, mx: 'auto' }}>
            {p.ctaBody}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={() => handleOpenWhatsApp(undefined, undefined, p.whatsappPhone)}
            >
              {p.whatsappLabel}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

LandingTotalEnProduccionConMem.propTypes = {
  themeMode: PropTypes.string,
};

export default LandingTotalEnProduccionConMem;
